import {forwardRef} from 'react';
import cx from 'classnames';
import Image from 'next/image';
import NextLink from 'next/link';

import {Button, Icon, Text} from '#component/base';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {
  getClassNamesForBreakpoint,
  getColorsForVariant,
  getTextVariantsForBreakpoint,
} from './CTACard.util';

import {CTACardProps} from './CTACard.types';

import * as css from './CTACard.css';

/**
 * This component renders the distinct CTACard that appears on the Dashboard
 * page. It contains a larger amount of design variations and a distinct layout
 * from the ImageCTACard used in many other places throughout the app.
 *
 * It includes a specific prop for setting Sprinkle's `gridColumnSpan`
 * value, as certain UI details are determined by how many columns a card
 * occupies in the Dashboard UI.
 */
export const CTACard = forwardRef<HTMLAnchorElement, CTACardProps>(
  (
    {
      className,
      columnSpan,
      fadeImage = true,
      headline = '',
      href,
      icon,
      imageUrl = '',
      lede = '',
      objectPosition,
      showButton = true,
      variant = 'primary',
      hasOverlay = true,
      btnColor,
      ...otherProps
    },
    ref,
  ) => {
    const renderedColSpan = columnSpan || [4, 8];
    const colors = getColorsForVariant(variant);
    const {isMobile, isTablet} = useViewportSize();
    const {largeButton, layout, smallButton} = getClassNamesForBreakpoint({
      columnSpan: renderedColSpan,
      isMobile,
      isTablet,
    });
    const {headlineVariant, ledeVariant} = getTextVariantsForBreakpoint({
      columnSpan: renderedColSpan,
      isMobile,
      isTablet,
    });

    return (
      <NextLink passHref href={href || '#'}>
        <span
          className={cx(css.root({variant}), sprinkles({colSpan: renderedColSpan}), className)}
          ref={ref}
          {...otherProps}>
          {hasOverlay && (
            <div className={css.image({fadeImage, variant})}>
              {imageUrl && (
                <Image
                  alt=""
                  aria-hidden
                  layout="fill"
                  objectFit="cover"
                  objectPosition={objectPosition}
                  src={imageUrl}
                />
              )}
            </div>
          )}
          {variant === 'textBottom' ? (
            <div>
              <div className={layout}>
                <div className={css.textBottom}>
                  <div
                    className={sprinkles({
                      flex: 'flex',
                      content: 'between',
                      items: 'end',
                      colSpan: 4,
                      gap: 16,
                    })}>
                    <div className={sprinkles({flex: 'flex', dir: 'col', gap: 10})}>
                      <Text color={colors.headline} variant={'heading2'} fontWeight={'light'}>
                        {headline}
                      </Text>
                    </div>
                  </div>

                  <Text
                    className={sprinkles({colSpan: 5, colStart: 5})}
                    color={colors.lede}
                    variant={'body'}
                    fontWeight={'light'}>
                    {lede}
                  </Text>

                  <div className={cx(sprinkles({flex: 'flex'}), css.centered)}>
                    <Button
                      className={cx(sprinkles({display: ['flex'], mT: ['auto'], width: 'half'}))}
                      aria-hidden
                      as="span"
                      icon="ArrowRight"
                      variant={btnColor ?? 'primary-dark'}>
                      {'Shop Now'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className={layout}>
                <div
                  className={sprinkles({
                    flex: 'flex',
                    content: 'between',
                    items: 'end',
                    colSpan: 4,
                    gap: 16,
                  })}>
                  <div className={sprinkles({flex: 'flex', dir: 'col', gap: 16})}>
                    {icon && <Icon color={colors.icon} name={icon} size={40} weight="light" />}

                    <Text color={colors.headline} variant={headlineVariant}>
                      {headline}
                    </Text>
                  </div>

                  {showButton && (
                    <Button
                      className={smallButton}
                      aria-hidden
                      as="span"
                      icon="ArrowRight"
                      variant={btnColor ?? colors.button}
                    />
                  )}
                </div>

                <Text
                  className={sprinkles({colSpan: 5, colStart: 5})}
                  color={colors.lede}
                  variant={ledeVariant}>
                  {lede}
                </Text>

                {showButton && (
                  <Button
                    className={largeButton}
                    aria-hidden
                    as="span"
                    icon="ArrowRight"
                    variant={btnColor ?? colors.button}
                  />
                )}
              </div>
            </div>
          )}
        </span>
      </NextLink>
    );
  },
);
